// Loan Financials — cross-loan Fees Ledger at /portfolio-admin/fees. A
// read-only roll-up of every loan fee with status + payer filters. There is
// no create affordance here — fees are entered from a loan's Financials
// panel; this page is the portfolio-wide tracking view. Filters live in the
// URL and are derived at parse time (no post-mount default effect).

import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Card,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  EmptyState,
  type StatusTone,
} from "@/design-system/components/ui";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import {
  LOAN_FEE_STATUSES,
  PAYMENT_CHANNELS,
  type LoanFeeStatus,
} from "@/api/portfolioAdmin/loanFeesApi";
import { useCrossLoanFees } from "@/hooks/portfolioAdmin/useLoanFees";

const STATUS_TONE: Record<LoanFeeStatus, StatusTone> = {
  pending: "warn",
  pending_review: "warn",
  approved: "info",
  billed: "info",
  collected: "ok",
  waived: "muted",
  canceled: "muted",
};

const STATUS_LABEL: Record<LoanFeeStatus, string> = Object.fromEntries(
  LOAN_FEE_STATUSES.map((s) => [s.value, s.label]),
) as Record<LoanFeeStatus, string>;

const CHANNEL_LABEL: Record<number, string> = Object.fromEntries(
  PAYMENT_CHANNELS.map((c) => [c.value, c.label]),
);

export default function FeesLedgerPage() {
  const [params, setParams] = useSearchParams();

  // Derive at parse time — "all" is the explicit cleared sentinel for status.
  const rawStatus = params.get("status");
  const status = rawStatus && rawStatus !== "all" ? rawStatus : undefined;
  const payer = params.get("payer")?.trim() || undefined;

  const query = useCrossLoanFees({ status, payer });
  const fees = useMemo(() => query.data?.fees ?? [], [query.data]);
  const summary = query.data?.summary;

  const writeParams = (mutate: (p: URLSearchParams) => void) => {
    const next = new URLSearchParams(params);
    mutate(next);
    setParams(next, { replace: true });
  };

  const setStatus = (value: string) =>
    writeParams((p) => {
      if (value) p.set("status", value);
      else p.delete("status");
    });

  const setPayer = (value: string) =>
    writeParams((p) => {
      if (value) p.set("payer", value);
      else p.delete("payer");
    });

  return (
    <div className="p-space-5">
      <PageHeader
        eyebrow="Loan financials"
        title="Fees ledger"
        description="Every loan fee across the portfolio. Fees are entered from a loan's Fee & Obligation tab; borrower-payable fees are reviewed by B3, then bill on the next statement and collect through the waterfall."
      />

      <div className="flex flex-wrap items-end gap-space-4 mb-space-4">
        <div
          className="inline-flex rounded-md border border-border-light overflow-hidden"
          role="group"
          aria-label="Status filter"
        >
          <StatusChip active={!status} onClick={() => setStatus("all")} testId="fees-status-all">
            All
          </StatusChip>
          {LOAN_FEE_STATUSES.map((s) => (
            <StatusChip
              key={s.value}
              active={status === s.value}
              onClick={() => setStatus(s.value)}
              testId={`fees-status-${s.value}`}
            >
              {s.label}
            </StatusChip>
          ))}
        </div>
        <label className="flex flex-col gap-space-1 text-small text-onyx">
          <span className="text-caption uppercase tracking-label text-gray-mid">Payer</span>
          <input
            className="border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
            value={params.get("payer") ?? ""}
            onChange={(e) => setPayer(e.target.value)}
            placeholder="Filter by payer"
            data-testid="fees-payer-filter"
          />
        </label>
      </div>

      {summary && (
        <>
          <div
            className="grid grid-cols-2 sm:grid-cols-4 gap-space-3 mb-space-1"
            data-testid="fees-ledger-summary"
          >
            <SummaryTile label="Total" value={formatCurrency(summary.totalAmount)} />
            <SummaryTile label="Unbilled" value={formatCurrency(summary.pendingAmount)} />
            <SummaryTile label="Billed" value={formatCurrency(summary.billedAmount)} />
            <SummaryTile label="Collected" value={formatCurrency(summary.collectedAmount)} />
          </div>
          <p className="text-caption text-gray-mid mb-space-4" data-testid="fees-total-note">
            &ldquo;Unbilled&rdquo; covers pending, in-review, and approved fees.
            &ldquo;Total&rdquo; counts every fee, including waived and canceled — it
            does not net to Unbilled + Billed + Collected.
          </p>
        </>
      )}

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : query.isError ? (
        <p className="text-small text-status-error" role="alert" data-testid="fees-ledger-error">
          Couldn't load fees. Try again.
        </p>
      ) : fees.length === 0 ? (
        <EmptyState
          title="No fees match"
          description="No loan fees match the current filters."
          variant="noResults"
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Loan</TH>
              <TH>Type</TH>
              <TH className="text-right">Amount</TH>
              <TH>Payer</TH>
              <TH>Channel</TH>
              <TH>Pool</TH>
              <TH>Status</TH>
              <TH>Incurred</TH>
            </TR>
          </THead>
          <TBody>
            {fees.map((fee) => (
              <TR key={fee.id} data-testid={`fees-ledger-row-${fee.id}`}>
                <TD>
                  <Link
                    to={`/portfolio-admin/loans/${fee.loanId}`}
                    className="text-gold-shadow hover:underline"
                  >
                    {fee.loanNumber ?? "View loan"}
                  </Link>
                </TD>
                <TD>{fee.feeType}</TD>
                <TD className="text-right tabular-nums">{formatCurrency(fee.amount)}</TD>
                <TD>{fee.payer}</TD>
                <TD className="text-gray-mid">
                  {CHANNEL_LABEL[fee.paymentChannel] ?? `Channel ${fee.paymentChannel}`}
                </TD>
                <TD>{fee.revenuePoolCode ?? "—"}</TD>
                <TD>
                  <StatusBadge tone={STATUS_TONE[fee.status]}>
                    {STATUS_LABEL[fee.status] ?? fee.status}
                  </StatusBadge>
                </TD>
                <TD className="text-gray-mid whitespace-nowrap">
                  {formatCalendarDate(fee.incurredDate)}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <Card className="p-space-3">
      <div className="text-caption uppercase tracking-label text-gray-mid">{label}</div>
      <div className="text-h3 font-display font-bold text-onyx tabular-nums">{value}</div>
    </Card>
  );
}

function StatusChip({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-1 text-small transition-colors duration-fast",
        active
          ? "bg-onyx text-white-pure font-semibold"
          : "bg-white-pure text-onyx hover:bg-white-warm",
      )}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
